import * as Yup from 'yup';
import {useForm, yupResolver} from '@mantine/form';
import {
    Avatar,
    Box,
    Button,
    Center,
    Checkbox,
    Container,
    createStyles,
    Divider,
    Grid,
    Group,
    Loader,
    LoadingOverlay,
    Modal,
    NativeSelect,
    Paper,
    TextInput,
    Title
} from '@mantine/core';
import React, {useEffect, useRef, useState} from "react";
import {CustomInput, CustomInputPassword} from '../hooks/CustomInput';
import {AppTable, HeadCell} from "../ui/app-table";
import {useMediaQuery} from '@mantine/hooks';
import {ApplicationForm} from "./application/application-form";
import {useLazyGetFileQuery, useLazyListenerQuery, useSpeakerMutation} from "../store/slice/customApiSlice";
import {useLocation} from "react-router-dom";
import {UseActions} from "../store";


type SignInPayload = {
    inn: string
    password: string
}

const schema = Yup.object({
    inn: Yup.string().required('Введите ПИН'),
    password: Yup.string().required('Введите пароль'),
});

type Lawyer = {
    id: number,
    address: string, // Место нахождения адвокатского кабинета/адвокатской организации
    fullName: string, // Полное имя
    licenseDate: string,
    licenseNumber: string,
    licenseStatus: string, // Статус лицензии
    phoneNumber: string // Номера телефонов
}

export const SignIn = () => {
    const {classes} = useStyles();
    const [loading, setLoading] = useState<boolean>(false);
    const [license, setLicense] = useState<string>('');
    const [opened, setOpened] = useState(false);
    const location = useLocation();
    const {login} = UseActions();

    const mobileSize = useMediaQuery('(max-width: 768px)')
    const [request] = useSpeakerMutation();
    const [getFile] = useLazyGetFileQuery();
    const [get] = useLazyListenerQuery();
    const linkRef = useRef<any>()

    const [licenseStatuses, setLicenseStatuses] = useState<{ label: string; value: number | null }[]>([]);
    const [territorial, setTerritorial] = useState<{ label: string; value: number | null }[]>([]);
    const [filter, setFilter] = useState<{
        licenseStatusId: number | null,
        query: string,
        territorialAdvocacyId: number | null,
        hasDeathAct: boolean
    }>({
        licenseStatusId: null,
        query: '',
        territorialAdvocacyId: null,
        hasDeathAct: false
    })
    const getLicenseStatus = async () => {
        await get({path: '/public/common-references/license-statuses'}).unwrap().then((data) => {
            const newList: { label: string; value: number | null }[] = [{
                label: 'Выберите статус лицензии',
                value: null
            }];
            data.map((item: any) => {
                newList.push({label: item.title, value: item.id})
            })
            setLicenseStatuses(newList);
        });
    }

    const getTerritorial = async () => {
        await get({path: '/public/common-references/territorial-advocacy'}).unwrap().then((data) => {
            const newList: { label: string; value: number | null }[] = [{
                label: 'Выберите территориальную адвокатуру',
                value: null
            }];
            data.map((item: any) => {
                newList.push({label: item.title, value: item.id})
            })
            setTerritorial(newList);
        });
    }


    const form = useForm({
        validate: yupResolver(schema),
        initialValues: {
            inn: '',
            password: '',
        },
    });

    const filterForm = useForm<{
        licenseStatusId: number | null,
        query: string,
        territorialAdvocacyId: number | null,
        hasDeathAct: boolean
    }>({
        initialValues: {
            licenseStatusId: null,
            territorialAdvocacyId: null,
            query: '',
            hasDeathAct: false
        }
    });

    const signInSubmit = form.onSubmit(async (values: SignInPayload) => {
        await request({
            path: `auth/login`,
            body: values,
        }).unwrap().then((data) => {
            login(data)
        })
    })

    const GetLicense = async (id: string | number, lang: string) => {
        if (!id || !lang) return
        const {data} = await getFile({
            path: `public/lawyers/${id}/license?language=${lang}`,
            method: "GET",
        }) as {
            data: string
        }
        return data
    }

    const headCells: HeadCell<Lawyer>[] = [
        {
            label: "№",
            sort: "ID",
            render: (row) => {
                return row.id
            },
        },
        {
            label: 'ФИО, Лицензия',
            render: (row) => <>{row.fullName}<br/>№{row.licenseNumber} от {row.licenseDate}</>,
        },
        {
            label: 'Номера телефонов',
            render: (row) => row.phoneNumber,
        },
        {
            label: 'Адрес',
            render: (row) => row.address,
        },
        {
            label: 'Статус лицензии',
            render: (row) => row.licenseStatus,
        },
        {
            label: 'Лицензия',
            render: (row) =>
                <Group noWrap position={"center"}>
                    <Button variant="default" onClick={() => GetLicense(row.id, 'RU').then((res) => {
                        if (res) setLicense(res)
                        setLoading(false)
                    })} size={"xs"}>
                        RU
                    </Button>
                    <Button variant="default" onClick={() => GetLicense(row.id, 'KG').then((res) => {
                        if (res) setLicense(res)
                        setLoading(false)
                    })} style={{
                        marginLeft: '10px',
                    }} size={"xs"}>
                        KG
                    </Button>
                </Group>
        }
    ]

    const openLicense = async () => {
        setLoading(true)
        if (location.search.includes('id')) {
            const url = new URL(window.location.href);
            const id = url.searchParams.get('id')?.toString();
            const lang = url.searchParams.get('lang')?.toUpperCase();

            if (id && lang) {
                return await GetLicense(id, lang)
            } else {
                console.error('ID or Lang parameter is missing');
            }
        }
    }

    useEffect(() => {
        getLicenseStatus()
        getTerritorial()
    }, [])

    useEffect(() => {
        openLicense().then((res) => {
            if (res) setLicense(res)
            setLoading(false)
        })
    }, [location.pathname])

    return (
        <div className={classes.wrapper}>
            {loading && <LoadingOverlay visible={loading}/>}
            <a style={{display: 'none'}} ref={linkRef}/>
            <Grid sx={{width: "100%", margin: "0"}}>
                <Grid.Col order={mobileSize ? 2 : 1} style={{flex: 3}}>
                    <Container style={{
                        color: 'white',
                        marginTop: '8%', padding: "0 3px"
                    }} size={'lg'}>
                        <Paper radius="md" p="xs" my={4}
                            // style={{background: 'linear-gradient(to bottom, #485563 0%, #29323c 100%)'}}
                        >
                            <form onSubmit={filterForm.onSubmit((values) => {
                                setFilter(values)
                            })}
                                // style={{
                                //   background: 'linear-gradient(to bottom, #485563 0%, #29323c 100%)',
                                //   color: 'white',
                                // }}
                            >
                                <Grid>
                                    <Grid.Col xs={12} md={6}>
                                        <NativeSelect
                                            {...filterForm.getInputProps('licenseStatusId')}
                                            label="Статус лицензии"
                                            data={licenseStatuses ? licenseStatuses : []}
                                            onChange={(e) => {
                                                filterForm.setFieldValue('licenseStatusId', Number(e.target.value))
                                            }}
                                            size={mobileSize ? 'xs' : 'sm'}
                                            //   style={{
                                            //     color: 'white',
                                            //     background: 'linear-gradient(to bottom, #485563 0%, #29323c 100%)',
                                            // }}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={12} md={6}>
                                        <NativeSelect
                                            {...filterForm.getInputProps('territorialAdvocacyId')}
                                            label="Территориальная адвокатура"
                                            data={territorial ? territorial : []}
                                            onChange={(e) => {
                                                filterForm.setFieldValue('territorialAdvocacyId', Number(e.target.value))
                                            }}
                                            size={mobileSize ? 'xs' : 'sm'}
                                            // style={{
                                            //   color: 'white',
                                            //   background: 'linear-gradient(to bottom, #485563 0%, #29323c 100%)',
                                            // }}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={12} lg={6}>
                                        <TextInput
                                            {...filterForm.getInputProps('query')}
                                            label="Ключевое слово"
                                            placeholder="Введите ключевое слово"
                                            size={mobileSize ? 'xs' : 'sm'}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={12} lg={3}>
                                        <Checkbox
                                            label={"Почившие адвокаты"}
                                            checked={filterForm.values.hasDeathAct}
                                            style={{
                                                marginTop: '2rem'
                                            }}
                                            onChange={(e) => {
                                                filterForm.setFieldValue(`hasDeathAct`, e.currentTarget.checked)
                                            }}
                                        />
                                    </Grid.Col>
                                    <Grid.Col xs={12} lg={3} style={{
                                        display: 'flex',
                                        alignItems: 'end',
                                        flexDirection: 'row-reverse'
                                    }}>
                                        <Group position="right" style={{marginTop: mobileSize ? "0" : "3px"}}>
                                            <Button
                                                variant="subtle"
                                                onClick={() => {
                                                    filterForm.reset();
                                                    setFilter({
                                                        licenseStatusId: null,
                                                        territorialAdvocacyId: null,
                                                        query: '',
                                                        hasDeathAct: false
                                                    })
                                                }}
                                                size={mobileSize ? 'xs' : 'sm'}
                                                color='dark'>Сбросить</Button>
                                            <Button type="submit" variant="subtle" color='dark'
                                                    size={mobileSize ? 'xs' : 'sm'}
                                            >Поиск</Button>
                                        </Group>
                                    </Grid.Col>
                                </Grid>
                            </form>
                        </Paper>
                        <AppTable headCells={headCells} extraFilter={filter} searchApiUrl={`/public/lawyers/search`}/>
                    </Container>
                </Grid.Col>
                <Grid.Col order={mobileSize ? 1 : 2}
                          sx={{flex: 1, padding: "0", paddingTop: "6%", paddingRight: mobileSize ? "0" : "2%"}}>
                    <Container size={420}>
                        <Grid sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                            <Avatar sx={{
                                marginBottom: '2rem',
                                width: '10rem',
                                height: '10rem',
                            }} src="./logo.png"/>
                            <Title order={2} className={classes.title} align="center" mb={50}>
                                Личный кабинет адвоката
                            </Title>
                        </Grid>

                        <Paper radius="md" p="xl" sx={{
                            background: "linear-gradient(to bottom, #485563 0%, #29323c 100%)",
                            color: 'white',
                        }} className={classes.authForm}>

                            <Title order={3} sx={{
                                fontWeight: 500,
                                fontSize: 28,
                            }} align="center" mb={25}>
                                Авторизация
                            </Title>

                            <form onSubmit={signInSubmit}>
                                <Center>
                                    <Box sx={{display: "flex", flexDirection: "column", width: "90%", gap: "10px"}}>
                                        <CustomInput
                                            label="ПИН"
                                            name={'inn'}
                                            // onChange={(e) => setInn(e.target.value)}
                                            {...form.getInputProps('inn')}
                                            maxLength={14}
                                        />
                                        <CustomInputPassword
                                            label="Пароль"
                                            {...form.getInputProps('password')}
                                        />
                                    </Box>
                                </Center>
                                <Group position="center" mt="xl" sx={{gap: "0"}}>
                                    <Button type="submit" variant='filled' color={'gray'}>Войти</Button>
                                    <Divider/>
                                </Group>
                                <Divider labelPosition="center" my="lg"/>
                                <Title order={3} sx={{
                                    fontWeight: 500,
                                    fontSize: 16,
                                }} align="center" mb={25}>
                                    Подать заявку на получение лицензии адвоката
                                </Title>
                                <Group position="center" mt="xl">
                                    <Button onClick={() => setOpened(true)} variant='filled' color={'gray'}>Подать
                                        заявку</Button>
                                </Group>
                            </form>
                        </Paper>
                    </Container>
                </Grid.Col>
            </Grid>
            {/*application*/}
            <Modal
                opened={!!license?.length}
                centered
                overlayOpacity={0.55}
                overlayBlur={3}
                onClose={() => {
                    setLicense('')
                    const url = new URL(window.location.href);
                    const id = url.searchParams.get('id')?.toString();
                    const lang = url.searchParams.get('lang')?.toUpperCase();
                    if (id && lang) {
                        url.searchParams.delete('id');
                        url.searchParams.delete('lang');
                        window.history.replaceState({}, '', url.toString());
                    }
                }}
                title={"Лицензия"}
                size={mobileSize ? 'full' : "70vw"}
                fullScreen={mobileSize}
                transition={"fade"}
                transitionDuration={500}
                transitionTimingFunction={"ease"}
                overflow={"outside"}
            >
                {
                    !license?.length ? <Loader/> :
                        <iframe src={license} width="100%" height="100%" frameBorder="0" style={{
                            height: '80vh'
                        }}
                        />
                }
            </Modal>
            <Modal
                opened={opened}
                centered
                overlayOpacity={0.55}
                overlayBlur={3}
                onClose={() => setOpened(false)}
                closeOnClickOutside={false}
                size={mobileSize ? "100%" : "90%"}
                fullScreen={mobileSize}
                title="Подача заявки"
                transition="fade"
                transitionDuration={600}
                transitionTimingFunction="ease"
                overflow={"outside"}
                sx={{fontSize: "20px"}}
            >
                <ApplicationForm exit={() => setOpened(false)}/>
            </Modal>
        </div>
    );
}


const useStyles = createStyles((theme) => ({
    wrapper: {
        minHeight: '100dvh',
        display: 'flex',
        background: window.innerWidth < 800 ? "#082032" : "url(./background_advocacy.jpg)",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundSize: 'cover',
        opacity: 0.9,
    },

    title: {
        color: 'white',
        margin: 'auto',
        fontFamily: `Greycliff CF, ${theme.fontFamily}`,
    },
    authForm: {
        transition: 'all 0.3s ease',
        "&:hover": {
            transform: 'scale(1.04)',
        }
    },
    dropZone: {},
    modalLabel: {
        display: 'flex',
        margin: "0",
        marginTop: "10px",
        fontSize: window.innerWidth > 800 ? "18px" : "15px",
        marginBottom: "5px",
    }
}));
